import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api } from "@/api/client.ts";
import { toast } from "sonner";
import eventBus from "@/utils/eventBus.ts";
import { useTranslation } from "@/locales";

interface EditTitleDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onChangeSuccess?: (title: string) => void;
  dialogTitle?: string;
  topicNo?: string;
}

const EditTitleDialog = ({
  isOpen,
  onOpenChange,
  dialogTitle,
  topicNo,
  onChangeSuccess,
}: EditTitleDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 错误信息
  const [errorMessage, setErrorMessage] = useState("");
  type UserFormValues = z.infer<typeof formSchema>;
  const formSchema = z.object({
    name: z
      .string()
      .min(1, {
        message: t("chat.titleRequired"),
      })
      .max(30, {
        message: t("chat.titleMaxLength"),
      }),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: dialogTitle ?? "",
    },
  });

  useEffect(() => {
    form.reset({
      name: dialogTitle ?? "",
    });
  }, [dialogTitle]);
  const onSubmit = async (data: UserFormValues) => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");
      console.log(data);
      // return false

      // 调用新增用户API
      const response: any = await api.post(
        "/v1/chat/topic/rename",
        {},
        {
          params: {
            topicNo: topicNo ?? 0,
            ...data,
          },
        }
      );
      console.log(response);
      if (response.topicName) {
        // 接口成功 - 关闭弹窗并触发回调
        onOpenChange(false);
        onChangeSuccess?.(response.topicName);
        toast.success(t("chat.titleUpdateSuccess"));
      } else {
        // 接口返回错误信息
        setErrorMessage(response.message || t("chat.titleUpdateFailed"));
      }
    } catch (error) {
      // 捕获网络等异常错误
      setErrorMessage(t("chat.networkError"));
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("chat.editTitle")}</DialogTitle>
        </DialogHeader>
        {/* 错误提示 */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={t("chat.titlePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("chat.submitting") : t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTitleDialog;
